import React, { useState } from 'react';

import { Badge } from '../Badge';
import { Checkbox } from '../Checkbox';
import { Modal } from '../Modal';
import { Text } from '../Text/Text';
import { BeforeAfter, OutcomeRow, ValueBox } from './SyncReviewParts';
import {
  TICKETS_TO_CAB,
  type SyncDecision,
  type SyncProposal,
} from '../../services/requirementSyncApi';
import { ACTION_BADGE, fieldLabel } from './syncReviewFormat';

import type { IRequirementSyncReviewProps } from './RequirementSyncReview.types';

const ProposalCard: React.FC<{
  proposal: SyncProposal;
  accepted: boolean;
  onToggle: (accepted: boolean) => void;
}> = ({ proposal, accepted, onToggle }) => {
  const badge = ACTION_BADGE[proposal.action];
  const blocked = Boolean(proposal.blockedReason);
  const isCab = proposal.kind === 'cab';
  const itemName = isCab ? 'CAB item' : 'ticket';
  return (
    <div className="flex flex-col gap-bg-v2-md border border-border-secondary rounded-bg-v2-md p-bg-v2-lg min-w-0">
      <div className="flex items-start gap-bg-v2-md">
        <Checkbox
          size="sm"
          checked={!blocked && accepted}
          disabled={blocked}
          onChange={e => onToggle(e.target.checked)}
          aria-label={`Apply change to ${proposal.itemId ?? proposal.title}`}
          containerClassName="!w-auto flex-shrink-0 pt-bg-v2-xxs"
        />
        <div className="flex flex-col gap-bg-v2-xxs min-w-0 flex-1">
          <div className="flex items-center gap-bg-v2-sm flex-wrap">
            <Badge tone={badge.tone}>{badge.label}</Badge>
            {isCab && proposal.sectionLabel && (
              <Text variant="text-sm" weight="medium" color="secondary" as="span">{proposal.sectionLabel}</Text>
            )}
            {!isCab && proposal.itemId && (
              <Text variant="text-sm" weight="semibold" color="primary" as="span">{proposal.itemId}</Text>
            )}
            {proposal.status && (
              <Badge tone={blocked ? 'warning' : 'neutral'} className="ml-auto">{proposal.status}</Badge>
            )}
          </div>
          <Text variant="text-sm" color={isCab ? 'primary' : 'secondary'} weight={isCab ? 'semibold' : 'regular'} className="break-words">
            {proposal.title}
          </Text>
          {blocked && (
            <Text variant="text-xs" color="warning">
              {proposal.blockedReason === 'in progress'
                ? "Someone is working on this ticket, so it won't be deleted. Close or update it yourself if it's no longer needed."
                : `This ${itemName} is ${proposal.blockedReason}, so the change is shown for your information and will not be applied.`}
            </Text>
          )}
          {!blocked && !isCab && proposal.action === 'update' && proposal.status === 'In Progress' && (
            <Text variant="text-xs" color="warning">
              This ticket is in progress. Let whoever is working on it know about this change.
            </Text>
          )}
        </div>
      </div>

      {proposal.action === 'delete' ? (
        <div className="flex flex-col gap-bg-v2-xxs">
          <Text variant="text-sm" color="secondary">
            {isCab
              ? 'This item will be removed from the CAB.'
              : blocked
                ? 'The CAB no longer contains what this ticket is for.'
                : 'The CAB no longer contains what this ticket is for, so it will be deleted.'}
          </Text>
          {isCab && proposal.stillLinked.length > 0 && (
            <Text variant="text-xs" color="warning">
              Open tickets that still point to it: {proposal.stillLinked.join(', ')}
            </Text>
          )}
        </div>
      ) : (
        proposal.fields.map(f => (
          <div key={f.field} className="flex flex-col gap-bg-v2-xs min-w-0">
            <Text variant="text-xs" weight="semibold" color="secondary">{fieldLabel(f.field)}</Text>
            {proposal.action === 'update' ? (
              <BeforeAfter before={f.old} after={f.removed ? null : f.new} />
            ) : (
              <ValueBox label="Value" value={f.new} tone="new" />
            )}
          </div>
        ))
      )}
      {/* The tickets a CAB change comes from; ticket changes have none. Who proposed it is not shown. */}
      {proposal.basis.length > 0 && (
        <Text variant="text-xs" color="tertiary">From: {proposal.basis.join(', ')}</Text>
      )}
    </div>
  );
};

export const RequirementSyncReview: React.FC<IRequirementSyncReviewProps> = ({
  isOpen,
  job,
  applying = false,
  onApply,
  onDiscard,
  onClose,
}) => {
  const proposals = job.result?.proposals ?? [];
  const outcomes = job.result?.outcomes ?? [];
  const toCab = job.ruleId === TICKETS_TO_CAB;
  const unhandled = outcomes.filter(o => o.outcome === 'unhandled').length;
  const [decisions, setDecisions] = useState<Record<string, SyncDecision>>(() =>
    Object.fromEntries(proposals.map(p => [p.proposalId, p.blockedReason ? 'rejected' : 'accepted'])),
  );
  const acceptedCount = proposals.filter(p => !p.blockedReason && decisions[p.proposalId] === 'accepted').length;
  const submitLabel = proposals.length === 0
    ? (unhandled > 0 ? 'Done' : 'Mark as up to date')
    : acceptedCount === 0
      ? 'Apply no changes'
      : `Apply ${acceptedCount} change${acceptedCount === 1 ? '' : 's'}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={toCab ? 'Review CAB updates' : 'Review ticket updates'}
      size="xl"
      onSubmit={() => onApply(decisions)}
      submitLabel={applying ? 'Applying…' : submitLabel}
      submitDisabled={applying}
      // Also disables Discard while either one runs: both save.
      submitLoading={applying}
      // Discard rejects every change; with none proposed it would only repeat the submit button.
      onCancel={proposals.length > 0 ? onDiscard : undefined}
      cancelLabel="Discard All"
      buttonSize="small"
    >
      <div className="flex flex-col gap-bg-v2-lg">
        {/* The run's own summary (free text from the AI Model) is not shown: the cards below say what changes. */}
        {proposals.length === 0 ? (
          <Text variant="text-sm" color="primary">
            {unhandled > 0
              ? `${unhandled} ticket change${unhandled === 1 ? '' : 's'} could not be worked out and stay${unhandled === 1 ? 's' : ''} pending. Choose Done, then update the CAB again to retry ${unhandled === 1 ? 'it' : 'them'}.`
              : toCab
                ? 'The CAB does not need to change for these ticket edits. Mark them as up to date to clear the notice.'
                : 'No tickets need to change for these CAB changes. Mark them as up to date to clear the notice.'}
          </Text>
        ) : (
          <>
            <Text variant="text-xs" color="tertiary">
              {toCab
                ? 'Unchecked changes are not made, and the ticket edits they came from count as handled. Discard All rejects every change.'
                : 'Unchecked changes are not made and are not proposed again. After applying, the tickets count as up to date with the current CAB. Discard All rejects every change.'}
            </Text>
            {proposals.map(p => (
              <ProposalCard
                key={p.proposalId}
                proposal={p}
                accepted={decisions[p.proposalId] === 'accepted'}
                onToggle={on => setDecisions(d => ({ ...d, [p.proposalId]: on ? 'accepted' : 'rejected' }))}
              />
            ))}
          </>
        )}
        {outcomes.length > 0 && (
          <div className="flex flex-col gap-bg-v2-md border-t border-border-secondary pt-bg-v2-lg">
            <Text variant="text-sm" weight="semibold" color="primary">Tickets checked</Text>
            {outcomes.map(o => <OutcomeRow key={o.ticketId} outcome={o} />)}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RequirementSyncReview;
